import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Typography,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useAuth } from "../AuthContext";
import useAuthenticatedFetch from "../hooks/useAuthenticatedFetch";
import { normalizeSlotBlocks, updateSlideSlotBlocks } from "../utils/layoutSlotUtils";
import {
  LAYOUT_BUILDER_DESIGN_DEFS,
  STATIC_BLOCKS,
  defaultLayoutConfig,
  getBlockLabel,
  getDropZoneBlockTitle,
  gridRowsToMinmax,
  makeDefaultAssignment,
  paletteItemFromDragId,
  sectionLabelForBlock,
  templateThumbUrls,
} from "../utils/layoutBuilderHelpers";

const PREVIEW_GRID_HEIGHT_PX = 530;
const SCROLL_EPS = 3;
const PALETTE_SCROLL_THRESHOLD = 11;
const PALETTE_ITEM_ROW_PX = 40;
const PALETTE_ITEM_GAP_PX = 8;

const paletteScrollListSx = (scrollHeight) => ({
  display: "flex",
  flexDirection: "column",
  gap: `${PALETTE_ITEM_GAP_PX}px`,
  minHeight: 0,
  ...(scrollHeight != null
    ? {
        height: scrollHeight,
        overflowY: "auto",
        overflowX: "hidden",
        overscrollBehavior: "contain",
        pr: 0.5,
        scrollbarGutter: "stable",
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: 8 },
        "&::-webkit-scrollbar-thumb": {
          borderRadius: 4,
          bgcolor: "action.disabledBackground",
        },
      }
    : {}),
});

const rootBoxSx = { width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", overflowX: "hidden" };

const stripScrollBtnSx = {
  flexShrink: 0,
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  "&:hover": { bgcolor: "action.hover" },
};

const slidesStripSx = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "stretch",
  gap: 1.25,
  overflowX: "auto",
  overflowY: "hidden",
  py: 1.5,
  px: 1.5,
  borderRadius: 2,
  bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"),
  border: (theme) => `1px solid ${theme.palette.divider}`,
  scrollbarGutter: "stable",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { height: 8 },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: 4,
    bgcolor: "action.disabledBackground",
  },
};

const slotTitleSx = {
  fontStyle: "italic",
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: "none",
};

const previewGridSx = (colCount, gridTemplateRows) => ({
  position: "relative",
  display: "grid",
  gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))`,
  gridTemplateRows: gridTemplateRows,
  gap: 2,
  alignItems: "stretch",
  paddingTop: 10,
  paddingLeft: 3,
  paddingRight: 3,
  paddingBottom: 3,
  height: PREVIEW_GRID_HEIGHT_PX,
  minHeight: PREVIEW_GRID_HEIGHT_PX,
  maxHeight: PREVIEW_GRID_HEIGHT_PX,
  overflow: "hidden",
  boxSizing: "border-box",
});

const LayoutBuilder = ({ onIsDirtyChange }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const fetchWithAuth = useAuthenticatedFetch();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sectionsPalette, setSectionsPalette] = useState([]);
  const [layoutConfig, setLayoutConfig] = useState(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarError, setSnackbarError] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  const slidesStripRef = useRef(null);
  const [canScrollSlidesPrev, setCanScrollSlidesPrev] = useState(false);
  const [canScrollSlidesNext, setCanScrollSlidesNext] = useState(false);

  const updateSlidesScrollEdges = useCallback(() => {
    const el = slidesStripRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollSlidesPrev(scrollLeft > SCROLL_EPS);
    setCanScrollSlidesNext(scrollLeft < scrollWidth - clientWidth - SCROLL_EPS);
  }, []);

  useLayoutEffect(() => {
    const el = slidesStripRef.current;
    if (!el) return;
    updateSlidesScrollEdges();
    const onScroll = () => updateSlidesScrollEdges();
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(() => updateSlidesScrollEdges());
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [updateSlidesScrollEdges, layoutConfig?.slides?.length]);

  const scrollSlidesStrip = useCallback((direction) => {
    const el = slidesStripRef.current;
    if (!el) return;
    const step = Math.max(260, Math.floor(el.clientWidth * 0.75));
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!layoutConfig?.slides?.length) return;
    const el = slidesStripRef.current;
    if (!el) return;
    el.querySelector(`[data-slide-index="${activeSlideIndex}"]`)?.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest",
    });
  }, [activeSlideIndex, layoutConfig?.slides?.length]);

  useEffect(() => {
    onIsDirtyChange?.(isDirty);
  }, [isDirty, onIsDirtyChange]);

  useEffect(() => {
    if (isMobile) {
      setLoading(false);
      setError(null);
      onIsDirtyChange?.(false);
      return;
    }

    const init = async () => {
      try {
        setLoading(true);
        setError(null);

        const sectionsRes = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/layout/getSections/${user.communityId}`,
        );
        if (!sectionsRes.ok) throw new Error(t("layoutBuilder.errors.loadSections"));
        const sections = await sectionsRes.json();
        setSectionsPalette(sections || []);

        const layoutRes = await fetch(
          `${process.env.REACT_APP_BASE_URL}/layout/getLayout?communityId=${user.communityId}&screenNumber=1`,
        );
        if (!layoutRes.ok) throw new Error(t("layoutBuilder.errors.loadLayout"));
        const existingLayout = await layoutRes.json();

        if (existingLayout) {
          setLayoutConfig({ templateName: "Armon", ...existingLayout });
        } else {
          setLayoutConfig(defaultLayoutConfig());
        }
        setActiveSlideIndex(0);
      } catch (e) {
        setError(e?.message || String(e));
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [user.communityId, fetchWithAuth, t, isMobile, onIsDirtyChange]);

  const activeSlide = layoutConfig?.slides?.[activeSlideIndex] || null;
  const activeDesignKey = activeSlide?.design || "4col";
  const activeDesign = LAYOUT_BUILDER_DESIGN_DEFS[activeDesignKey] || LAYOUT_BUILDER_DESIGN_DEFS["4col"];
  const templateName = layoutConfig?.templateName || "Armon";

  const previewThumb = useMemo(
    () => templateThumbUrls(templateName, activeDesignKey),
    [templateName, activeDesignKey],
  );

  const previewGridTemplateRows = useMemo(
    () => gridRowsToMinmax(activeDesign.gridTemplateRows),
    [activeDesign.gridTemplateRows],
  );

  const staticPalette = useMemo(() => STATIC_BLOCKS.map((b) => ({ ...b })), []);
  const paletteItems = useMemo(() => {
    const normalizedSections = (sectionsPalette || []).map((s) => ({
      blockType: s.blockType,
      sectionId: s.sectionId,
      sectionName: s.sectionName,
      category: s.category,
    }));
    return [
      ...staticPalette.map((s) => ({ ...s, sectionId: null, category: null })),
      ...normalizedSections,
    ];
  }, [sectionsPalette, staticPalette]);

  const paletteScrollHeight = useMemo(() => {
    if (paletteItems.length <= PALETTE_SCROLL_THRESHOLD) return null;
    return (
      PALETTE_SCROLL_THRESHOLD * PALETTE_ITEM_ROW_PX +
      (PALETTE_SCROLL_THRESHOLD - 1) * PALETTE_ITEM_GAP_PX
    );
  }, [paletteItems.length]);

  const slideUpdate = useCallback((updater) => {
    setLayoutConfig((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, slides: [...prev.slides] };
      updater(updated.slides[activeSlideIndex]);
      return updated;
    });
    setIsDirty(true);
  }, [activeSlideIndex]);

  const updateSlide = useCallback((index, updater) => {
    setLayoutConfig((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, slides: [...prev.slides] };
      updater(updated.slides[index]);
      return updated;
    });
    setIsDirty(true);
  }, []);

  const patchSlotBlock = useCallback(
    (slotId, blockId, patch) => {
      slideUpdate((slide) => {
        updateSlideSlotBlocks(slide, slotId, (blocks) =>
          blocks.map((b) => (b.id === blockId ? { ...b, ...patch } : b)),
        );
      });
    },
    [slideUpdate],
  );

  const removeSlotBlock = useCallback(
    (slotId, blockId) => {
      slideUpdate((slide) => {
        updateSlideSlotBlocks(slide, slotId, (blocks) => blocks.filter((b) => b.id !== blockId));
      });
    },
    [slideUpdate],
  );

  const handleAddSlide = useCallback(() => {
    setLayoutConfig((prev) => {
      if (!prev) return prev;
      const slides = [...prev.slides];
      slides.push({ id: `slide-${slides.length + 1}`, design: "4col", delaySec: 5, slots: {} });
      return { ...prev, slides };
    });
    setIsDirty(true);
  }, []);

  const handleDeleteSlide = useCallback((idx) => {
    setLayoutConfig((prev) => {
      if (!prev) return prev;
      if (prev.slides.length <= 1) return prev;
      return { ...prev, slides: prev.slides.filter((_, i) => i !== idx) };
    });
    setIsDirty(true);
    setActiveSlideIndex((prev) => {
      if (idx === prev) return 0;
      if (idx < prev) return prev - 1;
      return prev;
    });
  }, []);

  const handleSave = useCallback(async () => {
    try {
      if (!layoutConfig) return;
      const res = await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/layout/saveLayout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          communityId: user.communityId,
          screenNumber: 1,
          layoutConfig,
        }),
      });
      if (!res.ok) throw new Error(t("layoutBuilder.errors.save"));

      await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/events/${user.communityId}/refresh`, {
        method: "POST",
      });

      setSnackbarMessage(t("layoutBuilder.successSave"));
      setSnackbarError("");
      setIsDirty(false);
      setTimeout(() => setSnackbarMessage(""), 3000);
    } catch (e) {
      setSnackbarError(e?.message || String(e));
      setSnackbarMessage("");
    }
  }, [layoutConfig, user.communityId, fetchWithAuth, t]);

  const handleDragEnd = useCallback(
    (result) => {
      const { destination, source, draggableId } = result;
      if (!destination || source.droppableId !== "palette" || destination.droppableId === "palette") return;

      const paletteItem = paletteItemFromDragId(draggableId, paletteItems);
      if (!paletteItem) return;

      const slotId = destination.droppableId;
      slideUpdate((slide) => {
        updateSlideSlotBlocks(slide, slotId, (blocks) => [...blocks, makeDefaultAssignment(paletteItem)]);
      });
    },
    [paletteItems, slideUpdate],
  );

  if (isMobile) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          py: 6,
          px: 2,
          minHeight: 280,
        }}
      >
        <ComputerOutlinedIcon sx={{ fontSize: 56, color: "text.secondary", mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {t("layoutBuilder.desktopOnly.title")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 420 }}>
          {t("layoutBuilder.desktopOnly.message")}
        </Typography>
      </Box>
    );
  }

  if (loading) return <Typography>{t("layoutBuilder.loading")}</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!layoutConfig || !activeSlide) return <Typography>{t("layoutBuilder.noLayout")}</Typography>;

  return (
    <Box sx={rootBoxSx}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} sx={{ minWidth: 0, gap: 1 }}>
        <Typography variant="h4">{t("layoutBuilder.title")}</Typography>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={!isDirty}>
          {t("layoutBuilder.save")}
        </Button>
      </Box>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Box sx={{ mb: 2, width: "100%", maxWidth: "100%", minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%", maxWidth: "100%", minWidth: 0 }}>
            <IconButton
              size="small"
              aria-label={t("layoutBuilder.scrollLeft")}
              onClick={() => scrollSlidesStrip(-1)}
              disabled={!canScrollSlidesPrev}
              sx={stripScrollBtnSx}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Box ref={slidesStripRef} sx={slidesStripSx}>
              {layoutConfig.slides.map((slide, idx) => {
                const isActive = idx === activeSlideIndex;
                const thumb = templateThumbUrls(templateName, slide.design || "4col");
                return (
                  <Card
                    key={slide.id || idx}
                    data-slide-index={idx}
                    elevation={isActive ? 4 : 0}
                    onClick={() => setActiveSlideIndex(idx)}
                    variant="outlined"
                    sx={{
                      width: 240,
                      minWidth: 240,
                      flexShrink: 0,
                      height: 155,
                      padding: 0,
                      position: "relative",
                      cursor: "pointer",
                      transition: "box-shadow 0.2s, transform 0.2s, border-color 0.2s",
                      borderWidth: 2,
                      borderColor: isActive ? "primary.main" : "divider",
                      boxShadow: isActive
                        ? (theme) => `0 4px 20px ${theme.palette.mode === "dark" ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.12)"}`
                        : "none",
                      transform: isActive ? "scale(1.03)" : "none",
                      "&:hover": { borderColor: isActive ? "primary.main" : "action.active" },
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label={t("layoutBuilder.deleteSlide")}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSlide(idx);
                      }}
                      disabled={layoutConfig.slides.length <= 1}
                      sx={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        zIndex: 2,
                        bgcolor: "rgba(255,255,255,0.75)",
                        backdropFilter: "blur(6px)",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                    <CardContent
                      sx={{
                        p: 1.25,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                        "&:last-child": { pb: 1.25 },
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {t("layoutBuilder.slideTitle", { index: idx + 1, delay: slide.delaySec ?? 5 })}
                      </Typography>
                      <Box
                        sx={{
                          flex: 1,
                          height: 0,
                          borderRadius: 1,
                          overflow: "hidden",
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: "background.paper",
                        }}
                      >
                        <Box
                          component="img"
                          src={thumb.png}
                          onError={(e) => {
                            e.currentTarget.src = thumb.jpg;
                          }}
                          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          alt={`${templateName} ${slide.design}`}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}

              <Card
                variant="outlined"
                onClick={handleAddSlide}
                sx={{
                  minWidth: 100,
                  alignSelf: "stretch",
                  flexShrink: 0,
                  height: 155,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  borderStyle: "dashed",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                }}
              >
                <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 1.5, width: "100%" }}>
                  <AddIcon color="primary" fontSize="large" />
                </CardContent>
              </Card>
            </Box>
            <IconButton
              size="small"
              aria-label={t("layoutBuilder.scrollRight")}
              onClick={() => scrollSlidesStrip(1)}
              disabled={!canScrollSlidesNext}
              sx={stripScrollBtnSx}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
          <Grid item xs={12} md={3} sx={{ minWidth: 0, maxWidth: "100%" }}>
            <Card variant="outlined" sx={{ overflow: "hidden" }}>
              <CardContent sx={{ display: "flex", flexDirection: "column", minHeight: 0, pb: 2 }}>
                <Typography variant="h6" mb={1} sx={{ flexShrink: 0 }}>
                  {t("layoutBuilder.palette")}
                </Typography>
                <Droppable droppableId="palette">
                  {(provided) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={paletteScrollListSx(paletteScrollHeight)}
                    >
                      {paletteItems.map((item, index) => {
                        const draggableId = item.sectionId
                          ? `section:${item.blockType}:${item.sectionId}`
                          : `static:${item.blockType}`;
                        const label = getBlockLabel(item, t);
                        return (
                          <Draggable key={draggableId} draggableId={draggableId} index={index}>
                            {(dProvided) => (
                              <Card
                                ref={dProvided.innerRef}
                                {...dProvided.draggableProps}
                                {...dProvided.dragHandleProps}
                                variant="outlined"
                                sx={{
                                  cursor: "grab",
                                  flexShrink: 0,
                                  height: PALETTE_ITEM_ROW_PX,
                                  boxSizing: "border-box",
                                  display: "flex",
                                  alignItems: "center",
                                  px: 1,
                                  py: 0,
                                }}
                              >
                                <Typography variant="body2" noWrap title={label}>
                                  {label}
                                </Typography>
                              </Card>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={9} sx={{ minWidth: 0, maxWidth: "100%" }}>
            <Card variant="outlined">
              <CardContent>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  mb={2}
                >
                  <Typography variant="h6">
                    {t("layoutBuilder.previewTitle", { index: activeSlideIndex + 1, design: activeDesignKey })}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
                    <TextField
                      label={t("layoutBuilder.delayLabel")}
                      type="number"
                      size="small"
                      value={activeSlide.delaySec ?? 5}
                      onChange={(e) =>
                        updateSlide(activeSlideIndex, (s) => {
                          s.delaySec = Number(e.target.value) || 5;
                        })
                      }
                      sx={{ width: 120 }}
                    />
                    <Select
                      size="small"
                      value={activeSlide.design || "4col"}
                      onChange={(e) =>
                        updateSlide(activeSlideIndex, (s) => {
                          s.design = e.target.value;
                        })
                      }
                      displayEmpty
                      sx={{ minWidth: 100 }}
                    >
                      <MenuItem value="4col">4 col</MenuItem>
                    </Select>
                  </Box>
                </Box>

                <Box sx={previewGridSx(activeDesign.colCount, previewGridTemplateRows)}>
                  <Box
                    component="img"
                    src={previewThumb.png}
                    onError={(e) => {
                      e.currentTarget.src = previewThumb.jpg;
                    }}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "fill",
                      opacity: 0.5,
                      pointerEvents: "none",
                      userSelect: "none",
                      zIndex: 0,
                    }}
                    alt=""
                  />

                  {activeDesign.slotDefs.map((slotDef) => {
                    const slotBlocks = normalizeSlotBlocks(activeSlide.slots?.[slotDef.id]);
                    return (
                      <Droppable key={slotDef.id} droppableId={slotDef.id}>
                        {(slotProvided) => (
                          <Box
                            ref={slotProvided.innerRef}
                            {...slotProvided.droppableProps}
                            style={{
                              gridColumn: `${slotDef.colStart} / ${slotDef.colEnd}`,
                              gridRow: `${slotDef.rowStart} / ${slotDef.rowEnd}`,
                              border: "2px dashed rgba(0,0,0,0.12)",
                              borderRadius: 8,
                              padding: 8,
                              background: "rgba(0,0,0,0.02)",
                              boxSizing: "border-box",
                              position: "relative",
                              zIndex: 1,
                              minHeight: 0,
                              minWidth: 0,
                              height: "100%",
                              overflow: "auto",
                            }}
                          >
                            {slotBlocks.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                {t("layoutBuilder.dropHere")}
                              </Typography>
                            ) : (
                              <Box display="flex" flexDirection="column" gap={1}>
                                {slotBlocks.map((assignment, blockIdx) => {
                                  const sectionLabel = sectionLabelForBlock(assignment, sectionsPalette);
                                  return (
                                  <Box
                                    key={assignment.id}
                                    sx={{
                                      pb: 1,
                                      borderBottom: blockIdx < slotBlocks.length - 1 ? "1px solid" : "none",
                                      borderColor: "divider",
                                    }}
                                  >
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="subtitle2" sx={slotTitleSx}>
                                          {getDropZoneBlockTitle(assignment, t)}
                                        </Typography>
                                        {sectionLabel ? (
                                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                            {sectionLabel}
                                          </Typography>
                                        ) : null}
                                      </Box>
                                      <IconButton
                                        size="small"
                                        aria-label={t("layoutBuilder.removeBlock")}
                                        color="error"
                                        onClick={() => removeSlotBlock(slotDef.id, assignment.id)}
                                        sx={{ flexShrink: 0, mt: -0.5 }}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Box>

                                    {assignment.blockType === "ZMANIM" ||
                                    assignment.blockType === "NEXT_RDV" ||
                                    assignment.blockType === "COURSES_WEEKLY" ? (
                                      <FormControlLabel
                                        sx={{ mt: 0.5 }}
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={Boolean(assignment.autoScroll)}
                                            onChange={(e) =>
                                              patchSlotBlock(slotDef.id, assignment.id, {
                                                autoScroll: e.target.checked,
                                              })
                                            }
                                          />
                                        }
                                        label={t("layoutBuilder.autoScroll")}
                                      />
                                    ) : null}
                                  </Box>
                                  );
                                })}
                                <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
                                  {t("layoutBuilder.dropToAddMore")}
                                </Typography>
                              </Box>
                            )}
                            {slotProvided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DragDropContext>

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage("")}
        message={snackbarMessage}
      />
      <Snackbar
        open={!!snackbarError}
        autoHideDuration={3000}
        onClose={() => setSnackbarError("")}
        message={snackbarError}
      />
    </Box>
  );
};

export default LayoutBuilder;
